import React from 'react';
import { MapPin } from 'lucide-react';
import CategorySelector from './CategorySelector';
import VisibilityToggle from './VisibilityToggle';
import GoogleCalendarConnect from './GoogleCalendarConnect';

type AddressVisibility = 'public' | 'subscribers' | 'private';

interface BasicSettingsFormProps {
  name: string;
  description: string;
  categoryId: string;
  demoVideoUrl: string;
  isPublic: boolean;
  physicalAddress: string;
  addressVisibility: AddressVisibility;
  validationErrors: Record<string, string>;
  googleCalendarId: string | null;
  isGoogleConnected: boolean;
  isSyncing: boolean;
  isConnecting: boolean;
  syncResult: { synced: number; failed: number } | null;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onDemoVideoUrlChange: (value: string) => void;
  onIsPublicChange: (value: boolean) => void;
  onPhysicalAddressChange: (value: string) => void;
  onAddressVisibilityChange: (value: AddressVisibility) => void;
  onGoogleConnect: () => void;
  onGoogleReconnect: () => void;
  onSubmit: (e: React.FormEvent) => void;
  saving: boolean;
}

export default function BasicSettingsForm({
  name,
  description,
  categoryId,
  demoVideoUrl,
  isPublic,
  physicalAddress,
  addressVisibility,
  validationErrors,
  googleCalendarId,
  isGoogleConnected,
  isSyncing,
  isConnecting,
  syncResult,
  onNameChange,
  onDescriptionChange,
  onCategoryChange,
  onDemoVideoUrlChange,
  onIsPublicChange,
  onPhysicalAddressChange,
  onAddressVisibilityChange,
  onGoogleConnect,
  onGoogleReconnect,
  onSubmit,
  saving,
}: BasicSettingsFormProps) {
  return (
    <form onSubmit={onSubmit} className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Basic Settings</h2>

      <div className="space-y-8">
        {/* Google Calendar Connection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Google Calendar
          </label>
          <GoogleCalendarConnect
            isConnected={isGoogleConnected}
            googleCalendarId={googleCalendarId}
            isSyncing={isSyncing}
            isConnecting={isConnecting}
            syncResult={syncResult}
            onConnect={onGoogleConnect}
            onReconnect={onGoogleReconnect}
          />
        </div>

        {/* Name field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Calendar Name*
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="My Calendar"
          />
          {validationErrors.name && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
          )}
        </div>

        {/* Description field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-500">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={3}
            maxLength={200}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief description of your calendar"
          />
          <p className="mt-1 text-sm text-gray-500">
            {description.length}/200 characters
          </p>
          {validationErrors.description && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.description}</p>
          )}
        </div>

        {/* Physical Address field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Physical Address <span className="text-gray-500">(optional)</span>
          </label>
          <div className="mt-1 flex rounded-md shadow-sm">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
              <MapPin className="h-4 w-4" />
            </span>
            <input
              type="text"
              value={physicalAddress}
              onChange={(e) => onPhysicalAddressChange(e.target.value)}
              className="flex-1 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              placeholder="123 Main St, City, State 12345"
            />
          </div>
          {physicalAddress && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address Visibility
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => onAddressVisibilityChange('public')}
                  className={`px-3 py-2 text-sm border rounded-md ${
                    addressVisibility === 'public'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  Public
                </button>
                <button
                  type="button"
                  onClick={() => onAddressVisibilityChange('subscribers')}
                  className={`px-3 py-2 text-sm border rounded-md ${
                    addressVisibility === 'subscribers'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  Subscribers
                </button>
                <button
                  type="button"
                  onClick={() => onAddressVisibilityChange('private')}
                  className={`px-3 py-2 text-sm border rounded-md ${
                    addressVisibility === 'private'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  Private
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {addressVisibility === 'public' && 'Address will be visible to everyone'}
                {addressVisibility === 'subscribers' && 'Only subscribers can see the address'}
                {addressVisibility === 'private' && 'Only you can see the address'}
              </p>
            </div>
          )}
        </div>

        {/* Category field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category*
          </label>
          <CategorySelector
            selectedId={categoryId}
            onChange={onCategoryChange}
          />
          {validationErrors.category && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.category}</p>
          )}
        </div>

        {/* Demo Video URL field */}
        <div>
          <label htmlFor="demoVideoUrl" className="block text-sm font-medium text-gray-700">
            Add a video URL to showcase your calendar
          </label>
          <input
            type="url"
            id="demoVideoUrl"
            value={demoVideoUrl}
            onChange={(e) => onDemoVideoUrlChange(e.target.value)}
            placeholder="YouTube, Vimeo, or Dailymotion URL"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-1 text-sm text-gray-500">
            Add a video URL to showcase your calendar (supports YouTube, Vimeo, and Dailymotion)
          </p>
          {validationErrors.demoVideo && (
            <p className="mt-1 text-sm text-red-500">{validationErrors.demoVideo}</p>
          )}
        </div>

        <VisibilityToggle
          isPublic={isPublic}
          onChange={onIsPublicChange}
        />

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </form>
  );
}
